var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Dialog, Grid, Typography } from "@mui/material";
import { Cancel as CancelIcon } from "@mui/icons-material";
import { getDisplayPaymentTermsData } from "../../SharedFunctions";
import { resolvePaymentTermsForDisplay } from "../../PaymentTerms/DraftOrderPaymentTerms";
import { useDeviceQueries } from "../../../shared/Utilites";
import { OrderRowDetailKind } from "./allOrdersRowDetails";
import { CommonOrderDetailsDesktop } from "./CommonOrderDetailsDesktop";
import { CommonOrderDetailsMobile } from "./CommonOrderDetailsMobile";
import "../STReports/SalesOrderDetails.css";
/**
 * Shared order-detail dialog (All, Paid, Unpaid, Draft tabs). Body UI is split into
 * {@link CommonOrderDetailsDesktop} and {@link CommonOrderDetailsMobile}.
 */
export function CommonOrderDetailsDialogs(props) {
    var t = useTranslation().t;
    var detailKind = props.detailKind, onClose = props.onClose, shopCurrency = props.shopCurrency, currency = props.currency, selectFeatures = props.selectFeatures, standardProducts = props.standardProducts, standardClient = props.standardClient, standardMoreActionsMode = props.standardMoreActionsMode, onStandardDuplicate = props.onStandardDuplicate, onStandardPrint = props.onStandardPrint, standardSourceRow = props.standardSourceRow, draftProducts = props.draftProducts, draftClient = props.draftClient, draftSourceRow = props.draftSourceRow, onDraftEdit = props.onDraftEdit, onDraftDuplicate = props.onDraftDuplicate, onDraftSendInvoice = props.onDraftSendInvoice, onDraftDelete = props.onDraftDelete, refundProducts = props.refundProducts, standardShipDateHideWhenNotFulfilled = props.standardShipDateHideWhenNotFulfilled, standardMobileLineItemsVariant = props.standardMobileLineItemsVariant;
    var isMobile = useDeviceQueries();
    var _a = useState(null), draftMoreMenuAnchor = _a[0], setDraftMoreMenuAnchor = _a[1];
    var handleDialogClose = function () {
        setDraftMoreMenuAnchor(null);
        onClose();
    };
    var dialogTitle = detailKind === OrderRowDetailKind.Standard
        ? t("yourSales_tableTitle")
        : detailKind === OrderRowDetailKind.Draft
            ? t("yourSalesDraft_tableTitle")
            : detailKind === OrderRowDetailKind.Refund
                ? t("refundOrderNumber_title")
                : "";
    var productSalesHeaders = useMemo(function () { return [
        { label: t("yourSales_column1") },
        { label: t("yourSales_column2") },
        { label: t("variant_title") },
        { label: "SKU" },
        { label: t("yourSales_column3") },
        { label: t("yourSales_column4") + "(" + shopCurrency + ")" },
        { label: t("viewProductModelColumn6") + "(" + shopCurrency + ")" },
        { label: t("yourSales_column5") },
        { label: t("yourSales_column6") },
        { label: t("yourSales_column7") + "(" + shopCurrency + ")" },
    ]; }, [t, shopCurrency]);
    var refundProductHeaders = useMemo(function () { return [
        { label: t("refundOrderNumber_column1") },
        { label: t("refundOrderNumber_column3") },
        { label: t("refundOrderNumber_column4") + "(" + shopCurrency + ")" },
        { label: t("refundOrderNumber_column5") + "(" + shopCurrency + ")" },
    ]; }, [t, shopCurrency]);
    var draftProductHeaders = useMemo(function () { return [
        { label: t("draftOrder_Product") },
        { label: t("draftOrder_ProductName") },
        { label: t("draftOrder_Size") },
        { label: t("draftOrder_Quantity") },
        { label: t("draftOrder_Price") },
        { label: t("draftOrder_Status") },
        { label: t("draftOrder_SubTotal") },
    ]; }, [t]);
    var displayPaymentTerms = useMemo(function () { return resolvePaymentTermsForDisplay(draftClient); }, [draftClient]);
    var displayPaymentTermsData = useMemo(function () { return getDisplayPaymentTermsData(displayPaymentTerms, draftClient === null || draftClient === void 0 ? void 0 : draftClient.paymentTerms); }, [displayPaymentTerms, draftClient === null || draftClient === void 0 ? void 0 : draftClient.paymentTerms]);
    var standardPaymentTermsForDisplay = useMemo(function () { return resolvePaymentTermsForDisplay(standardClient); }, [standardClient]);
    var standardDisplayPaymentTermsData = useMemo(function () { return getDisplayPaymentTermsData(standardPaymentTermsForDisplay, standardClient === null || standardClient === void 0 ? void 0 : standardClient.paymentTerms); }, [standardPaymentTermsForDisplay, standardClient === null || standardClient === void 0 ? void 0 : standardClient.paymentTerms]);
    var bodyProps = {
        detailKind: detailKind,
        shopCurrency: shopCurrency,
        currency: currency,
        selectFeatures: selectFeatures,
        productSalesHeaders: productSalesHeaders,
        draftProductHeaders: draftProductHeaders,
        refundProductHeaders: refundProductHeaders,
        standardProducts: standardProducts,
        standardClient: standardClient,
        standardDisplayPaymentTermsData: standardDisplayPaymentTermsData,
        standardMoreActionsMode: standardMoreActionsMode,
        standardSourceRow: standardSourceRow,
        onStandardDuplicate: onStandardDuplicate,
        onStandardPrint: onStandardPrint,
        draftProducts: draftProducts,
        draftClient: draftClient,
        draftSourceRow: draftSourceRow,
        displayPaymentTermsData: displayPaymentTermsData,
        draftMoreMenuAnchor: draftMoreMenuAnchor,
        setDraftMoreMenuAnchor: setDraftMoreMenuAnchor,
        onDraftEdit: onDraftEdit,
        onDraftDuplicate: onDraftDuplicate,
        onDraftSendInvoice: onDraftSendInvoice,
        onDraftDelete: onDraftDelete,
        refundProducts: refundProducts,
        standardShipDateHideWhenNotFulfilled: standardShipDateHideWhenNotFulfilled,
        standardMobileLineItemsVariant: standardMobileLineItemsVariant,
    };
    return (_jsx(Dialog, __assign({ open: detailKind !== null, maxWidth: "xl", fullWidth: true, onClose: handleDialogClose, PaperProps: detailKind === OrderRowDetailKind.Draft ? { className: "ST_OrderPopup_DialogPaper" } : undefined }, { children: _jsxs(Box, __assign({ className: "ST_SO_Dialog_Box" }, { children: [_jsxs(Grid, __assign({ container: true, spacing: 2, mb: 2 }, { children: [_jsx(Grid, __assign({ item: true, xs: 10, md: 10 }, { children: _jsx(Typography, __assign({ variant: "h6", sx: { textTransform: "capitalize" } }, { children: dialogTitle })) })), _jsx(Grid, __assign({ item: true, xs: 2, md: 2, sx: { textAlign: "right" } }, { children: _jsx(CancelIcon, { sx: { cursor: "pointer" }, onClick: handleDialogClose }) }))] })), isMobile ? _jsx(CommonOrderDetailsMobile, __assign({}, bodyProps)) : _jsx(CommonOrderDetailsDesktop, __assign({}, bodyProps))] })) })));
}
